import React from 'react';
import { useDispatch } from 'react-redux';
import { externalFile } from 'hellospa/page/prep-and-send/data/actions/file';
import {
  DropboxIntegration, UploadIntegrationType,
} from 'hellospa/page/prep-and-send/data/types/integration';
import ReAuthModal from './reauth-modal';
import dropboxIcon from 'images/external_logo_med_D.png';

type DropboxFile = {
  link: string,
  name: string,
}

type PickerOptions = {
  cancel: () => void,
  linkType: string,
  iframe: boolean,
  success: (files: DropboxFile[]) => void,
}

type Dropbox = {
  choose: (options: PickerOptions) => void,
}

declare global {
  interface Window { Dropbox: Dropbox; }
}

type Props = {
  settings: DropboxIntegration,
  shouldTriggerPicker: boolean,
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
}

const SCRIPT_SRC = 'https://www.dropbox.com/static/api/2/dropins.js';
const SCRIPT_ID = 'dropboxjs';
/**
 * Load the script to inject into the DOM (happens on component mount)
 * @param  {DropboxIntegration} settings
 * @return {void}
 */
function initialize(settings: DropboxIntegration) {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.setAttribute('nonce', settings.nonce);
  script.setAttribute('data-app-key', settings.key);

  return script;
}

export default function DropboxFilePicker({
  settings,
  shouldTriggerPicker,
  toggleIntegrationPicker,
}: Props) {
  const dispatch = useDispatch();

  React.useEffect(() => {
    const script = initialize(settings);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [settings]);

  React.useEffect(() => {
    // trigger picker here, also check if it's integrated already
    if (shouldTriggerPicker && settings.enabled && window.Dropbox != null) {
      const options: PickerOptions = {
        linkType: 'direct',
        iframe: true,

        success: (files: DropboxFile[]) => {
          // select the first value since multi select is disabled currently
          const file = files.length > 0 ? files[0] : null;

          if (file) {
            dispatch(externalFile(UploadIntegrationType.Dropbox, file.link, file.name));
          }
          // set the integration value in state to false in file-picker after retrieval or not
          toggleIntegrationPicker(UploadIntegrationType.Dropbox, false);
        },

        // clicking the close button on the file picker window
        cancel: () => {
          toggleIntegrationPicker(UploadIntegrationType.Dropbox, false);
        },
      };

      // initialize the Dropbox chooser (file picker)
      window.Dropbox.choose(options);
    }
  }, [dispatch, settings.enabled, shouldTriggerPicker, toggleIntegrationPicker]);

  // if clicked on the integration icon but it's not enabled,
  // trigger the modal to initiate auth
  if (shouldTriggerPicker && !settings.enabled) {
    return (
      <ReAuthModal
        integration={UploadIntegrationType.Dropbox}
        integrationIcon={dropboxIcon}
        isOpen={shouldTriggerPicker && !settings.enabled}
        onClose={() => toggleIntegrationPicker(UploadIntegrationType.Dropbox, false)} />
    );
  }

  return null;
}
